import React, { useEffect, useState } from 'react';

import { useReducedMotion } from '../hooks/useReducedMotion';
import { useTradingStore } from '../store';
import type { ClosedTrade } from '../types';

/**
 * Determines the intensity tier based on PnL percentage.
 * Returns 'small' | 'medium' | 'large' for scaling visual effects.
 */
function getIntensityTier(pnlPercent: number): 'small' | 'medium' | 'large' {
    const absPnl = Math.abs(pnlPercent);
    if (absPnl >= 1) return 'large';
    if (absPnl >= 0.5) return 'medium';
    return 'small';
}

/**
 * TradeResultOverlay - Full-screen edge glow effect for trade results.
 *
 * Features:
 * - Non-blocking (pointer-events: none)
 * - Green glow for wins, magenta for losses
 * - Intensity scales with PnL percentage
 * - Respects reduced motion preferences
 * - Auto-dismisses after animation completes
 *
 * Reads `overlayTrade` directly from the trading store (0 props).
 */
const TradeResultOverlay: React.FC = React.memo(() => {
    const trade = useTradingStore((s) => s.overlayTrade);
    const [isVisible, setIsVisible] = useState(false);
    const [currentTrade, setCurrentTrade] = useState<ClosedTrade | null>(null);
    const prefersReducedMotion = useReducedMotion();

    useEffect(() => {
        if (trade) {
            setCurrentTrade(trade);
            setIsVisible(true);

            // Auto-hide after animation completes
            // Duration matches CSS animation (3s for large, 2.5s for medium, 2s for small)
            const intensity = getIntensityTier(trade.pnlPercent);
            const duration =
                intensity === 'large'
                    ? 3000
                    : intensity === 'medium'
                        ? 2500
                        : 2000;

            const timer = setTimeout(() => {
                setIsVisible(false);
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [trade]);

    // Don't render if no trade or animation completed
    if (!isVisible || !currentTrade) return null;

    // Skip animations for reduced motion users
    if (prefersReducedMotion) return null;

    const isProfit = currentTrade.pnlValue >= 0;
    const intensity = getIntensityTier(currentTrade.pnlPercent);

    // Build class names for the overlay
    const overlayClasses = [
        'trade-result-overlay',
        isProfit ? 'trade-result-overlay--win' : 'trade-result-overlay--loss',
        `trade-result-overlay--${intensity}`,
    ].join(' ');

    return (
        <div
            className={overlayClasses}
            aria-hidden="true"
            data-testid="trade-result-overlay"
        />
    );
});

TradeResultOverlay.displayName = 'TradeResultOverlay';

export default TradeResultOverlay;
